import React, { useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/system';
import {
  Phone,
  PhoneIncoming,
  PhoneOff,
  MicOff,
  Grid3x3,
  Volume2,
  Mic,
  Captions,
  MessageCircleQuestion
} from 'lucide-react';
import {
  Card,
  Avatar,
  OptionFeedback,
  ReplyOptions,
  shuffledOrder,
  seedFrom
} from '../components/ui';
import {
  motion,
  fadeUp,
  fadeIn,
  popIn,
  ripple,
  wave,
  blink,
  ring,
  pulseRing,
  REDUCED,
  prefersReducedMotion
} from '../components/motion';
import { optionResult } from '../engine/scoring';
import { L, t } from '../strings';

// The caller's mood once you have answered.
const MOOD = { best: '😊', ok: '😐', bad: '😠' };

const WORD_MS = 65;
const PAUSE_MS = 550;

const words = (text) => text.split(/\s+/).filter(Boolean);

const mmss = (sec) =>
  `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;

// Rough length of the whole call, used when the step is revisited.
const estimateSeconds = (texts) =>
  Math.round(texts.reduce((n, x) => n + words(x).length, 0) / 2.6) + 8;

/* ---------- call screen ---------- */

const Shell = styled(Card)(({ theme }) => ({
  padding: 0,
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: { padding: 0 }
}));

const Screen = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: '22px 24px 20px',
  color: '#fff',
  textAlign: 'center',
  background:
    'radial-gradient(120% 90% at 50% 0%, rgba(185, 248, 51, 0.20), rgba(185, 248, 51, 0) 60%), #141414',
  [theme.breakpoints.down('sm')]: { padding: '18px 14px 16px' }
}));

const Status = styled('div')(({ live }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  padding: '4px 12px',
  borderRadius: 100,
  backgroundColor: 'rgba(255,255,255,0.08)',
  fontFamily: 'Rubik-Regular',
  fontSize: 13,
  lineHeight: '18px',
  color: live ? '#B9F833' : 'rgba(255,255,255,0.75)',
  fontVariantNumeric: 'tabular-nums',
  '& .dot': {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: '#B9F833',
    ...motion(`${blink} 1.4s ease-in-out infinite`)
  },
  '& .ringing': {
    transformOrigin: '50% 50%',
    ...motion(`${ring} 1s ease-in-out infinite`)
  }
}));

const AvatarWrap = styled('div')(() => ({
  position: 'relative',
  width: 84,
  height: 84,
  margin: '18px auto 12px'
}));

const Ripple = styled('span')(({ delay }) => ({
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  border: '2px solid rgba(185, 248, 51, 0.7)',
  ...motion(`${ripple} 1.8s ease-out ${delay}s infinite`)
}));

const Mood = styled('span')(() => ({
  position: 'absolute',
  right: -6,
  bottom: -4,
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: '#fff',
  boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 18,
  ...motion(`${popIn} 360ms ease-out both`)
}));

const Name = styled('div')(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 20,
  fontWeight: 500,
  lineHeight: '26px'
}));

const Role = styled('div')(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 13,
  lineHeight: '18px',
  color: 'rgba(255,255,255,0.6)',
  marginBottom: 14
}));

const Bars = styled('div')(({ active }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 4,
  height: 28,
  marginBottom: 16,
  '& i': {
    width: 4,
    height: 24,
    borderRadius: 4,
    backgroundColor: active ? '#B9F833' : 'rgba(255,255,255,0.18)',
    transform: 'scaleY(0.25)',
    transition: 'background-color 200ms ease',
    ...(active ? motion(`${wave} 0.9s ease-in-out infinite`) : {})
  },
  [REDUCED]: { '& i': { transform: active ? 'scaleY(0.7)' : 'scaleY(0.25)' } }
}));

const BAR_DELAYS = [0.1, 0.35, 0, 0.5, 0.2, 0.6, 0.15, 0.4, 0.05, 0.3, 0.55];

const Controls = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  gap: 18
}));

const Ctl = styled('span')(({ danger, dim }) => ({
  width: 46,
  height: 46,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: danger ? '#e5484d' : 'rgba(255,255,255,0.10)',
  color: '#fff',
  opacity: dim ? 0.35 : 1,
  transition: 'opacity 300ms ease'
}));

const AnswerButton = styled('button')(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '50%',
  border: 'none',
  cursor: 'pointer',
  backgroundColor: theme.palette.primary.salad,
  color: theme.palette.textPrimary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 8px',
  transition: 'transform 160ms ease',
  ...motion(`${pulseRing} 1.4s ease-out infinite`),
  '& svg': { transformOrigin: '50% 50%', ...motion(`${ring} 1s ease-in-out infinite`) },
  '&:hover': { transform: 'scale(1.06)' },
  '&:focus-visible': { outline: '3px solid #fff', outlineOffset: 3 },
  [REDUCED]: { '& svg': { animation: 'none' } }
}));

const AnswerLabel = styled('div')(() => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 13,
  color: 'rgba(255,255,255,0.75)'
}));

/* ---------- captions + reply ---------- */

const Transcript = styled('div')(({ theme }) => ({
  padding: '16px 24px 18px',
  backgroundColor: '#f6f7f3',
  borderBottom: `1px solid ${theme.palette.grey[100]}`,
  minHeight: 120,
  [theme.breakpoints.down('sm')]: { padding: '14px 12px' }
}));

const CaptionHead = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontFamily: 'Rubik-Regular',
  fontSize: 12,
  color: theme.palette.textTertiary.main,
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: 0.5
}));

const Line = styled('div')(({ mine, tight }) => ({
  display: 'flex',
  gap: 10,
  alignItems: 'flex-start',
  flexDirection: mine ? 'row-reverse' : 'row',
  marginTop: tight ? 4 : 12,
  ...motion(`${fadeUp} 280ms ease-out both`)
}));

const AvatarSlot = styled('div')(() => ({ width: 28, minWidth: 28 }));

const LineBody = styled('div')(({ theme, mine }) => ({
  maxWidth: 'min(600px, 86%)',
  fontFamily: 'Rubik-Regular',
  fontSize: 15,
  lineHeight: '22px',
  color: theme.palette.textPrimary.main,
  overflowWrap: 'anywhere',
  ...(mine
    ? { padding: '8px 12px', borderRadius: 14, borderTopRightRadius: 4, backgroundColor: '#e3f9b8' }
    : {}),
  '& b': {
    display: 'block',
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '18px',
    marginBottom: 2
  }
}));

const Caret = styled('span')(() => ({
  display: 'inline-block',
  width: 2,
  height: 16,
  marginLeft: 2,
  verticalAlign: -3,
  backgroundColor: '#79c912',
  ...motion(`${blink} 0.9s step-end infinite`)
}));

const Composer = styled('div')(({ theme }) => ({
  padding: '18px 20px 16px',
  backgroundColor: '#fff',
  [theme.breakpoints.down('sm')]: { padding: '14px 12px' }
}));

const Prompt = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontFamily: 'Rubik-Regular',
  fontSize: 15,
  fontWeight: 500,
  color: theme.palette.textPrimary.main,
  marginBottom: 12,
  ...motion(`${fadeIn} 300ms ease-out both`),
  '& svg': { color: '#4f9a04' }
}));

const Waiting = styled('div')(({ theme }) => ({
  fontFamily: 'Rubik-Regular',
  fontSize: 14,
  color: theme.palette.textDisabled.main
}));

export default function CallStep({ step, pack, answered, onSubmit, kpiLabels }) {
  const people = useMemo(
    () => Object.fromEntries(pack.people.map((p) => [p.key, p])),
    [pack.people]
  );
  const order = useMemo(
    () => shuffledOrder(step.options.length, seedFrom(pack.id + step.id)),
    [pack.id, step]
  );
  const messages = step.messages;
  const caller = people[messages[0]?.from];
  const texts = useMemo(() => messages.map((m) => L(m.text)), [messages]);

  const revisit = useRef(!!answered).current;
  const reduced = useRef(prefersReducedMotion()).current;
  const [phase, setPhase] = useState(revisit ? 'ended' : 'ringing'); // ringing | live | ended
  const [spoken, setSpoken] = useState(revisit ? messages.length : 0); // finished lines
  const [wordCount, setWordCount] = useState(0); // words of the current line
  const [elapsed, setElapsed] = useState(0);
  const [youTalking, setYouTalking] = useState(false);
  const allSpoken = spoken >= messages.length;
  const picked = answered ? step.options[answered.pick] : null;

  // Call timer.
  useEffect(() => {
    if (phase !== 'live') return undefined;
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [phase]);

  // Live captions: the caller's lines appear word by word.
  useEffect(() => {
    if (phase !== 'live' || allSpoken) return undefined;
    const total = words(texts[spoken]).length;
    if (reduced || wordCount >= total) {
      const id = setTimeout(
        () => {
          setSpoken((n) => n + 1);
          setWordCount(0);
        },
        reduced ? 300 : PAUSE_MS
      );
      return () => clearTimeout(id);
    }
    const id = setTimeout(() => setWordCount((n) => n + 1), wordCount === 0 ? 350 : WORD_MS);
    return () => clearTimeout(id);
  }, [phase, spoken, wordCount, allSpoken, texts, reduced]);

  // After you speak, the caller hangs up.
  useEffect(() => {
    if (!answered || phase === 'ended') return undefined;
    setYouTalking(true);
    const id = setTimeout(
      () => {
        setYouTalking(false);
        setPhase('ended');
      },
      reduced ? 200 : 1400
    );
    return () => clearTimeout(id);
  }, [answered, phase, reduced]);

  const pick = (idx) => onSubmit(optionResult(step.options[idx], idx));
  const skip = () => {
    if (phase === 'live' && !allSpoken) {
      setSpoken(messages.length);
      setWordCount(0);
    }
  };

  const callerTalking = phase === 'live' && !allSpoken && wordCount > 0;
  const duration = revisit ? estimateSeconds([...texts, picked ? L(picked.text) : '']) : elapsed;

  let status;
  if (phase === 'ringing')
    status = (
      <Status>
        <PhoneIncoming size={14} className="ringing" /> {t('incomingCall')}
      </Status>
    );
  else if (phase === 'live')
    status = (
      <Status live>
        <span className="dot" /> {mmss(elapsed)}
      </Status>
    );
  else
    status = (
      <Status>
        <PhoneOff size={14} /> {t('callEnded')} · {mmss(duration)}
      </Status>
    );

  return (
    <Shell>
      <Screen>
        {status}
        <AvatarWrap>
          {phase === 'ringing' && (
            <>
              <Ripple delay={0} />
              <Ripple delay={0.9} />
            </>
          )}
          <Avatar person={caller} size={84} />
          {phase === 'ended' && picked && <Mood title={L(caller?.name)}>{MOOD[picked.q]}</Mood>}
        </AvatarWrap>
        <Name>{L(caller?.name)}</Name>
        <Role>{L(caller?.role)}</Role>

        {phase === 'ringing' ? (
          <>
            <AnswerButton
              type="button"
              onClick={() => setPhase('live')}
              aria-label={t('answerCall')}>
              <Phone size={26} />
            </AnswerButton>
            <AnswerLabel>{t('answerCall')}</AnswerLabel>
          </>
        ) : (
          <>
            <Bars active={callerTalking || youTalking} aria-hidden="true">
              {BAR_DELAYS.map((d, i) => (
                <i key={i} style={{ animationDelay: `${d}s` }} />
              ))}
            </Bars>
            <Controls aria-hidden="true">
              <Ctl dim={phase === 'ended'}>
                <MicOff size={19} />
              </Ctl>
              <Ctl dim={phase === 'ended'}>
                <Grid3x3 size={19} />
              </Ctl>
              <Ctl dim={phase === 'ended'}>
                <Volume2 size={19} />
              </Ctl>
              <Ctl danger dim={phase === 'ended'}>
                <PhoneOff size={19} />
              </Ctl>
            </Controls>
          </>
        )}
      </Screen>

      {phase !== 'ringing' && (
        <Transcript onClick={skip} style={{ cursor: allSpoken ? 'default' : 'pointer' }}>
          <CaptionHead>
            <Captions size={14} /> {t('liveCaptions')}
          </CaptionHead>
          <div aria-live="polite">
            {messages.map((m, i) => {
              if (i > spoken) return null;
              const current = i === spoken && !allSpoken;
              if (current && wordCount === 0) return null;
              const p = people[m.from];
              const text = current ? words(texts[i]).slice(0, wordCount).join(' ') : texts[i];
              const cont = i > 0 && messages[i - 1].from === m.from;
              return (
                <Line key={i} tight={cont}>
                  <AvatarSlot>{!cont && <Avatar person={p} size={28} />}</AvatarSlot>
                  <LineBody>
                    {!cont && <b style={{ color: p?.color }}>{L(p?.name)}</b>}
                    {text}
                    {current && <Caret />}
                  </LineBody>
                </Line>
              );
            })}
            {picked && (
              <Line mine>
                <Avatar
                  person={{ initials: t('you'), color: '#B9F833' }}
                  size={28}
                  style={{ color: '#222' }}
                />
                <LineBody mine>
                  <b style={{ color: '#4f9a04' }}>{t('you')}</b>
                  {L(picked.text)}
                </LineBody>
              </Line>
            )}
          </div>
        </Transcript>
      )}

      {phase !== 'ringing' && (
        <Composer>
          {!answered && allSpoken && (
            <>
              {step.prompt && (
                <Prompt>
                  <MessageCircleQuestion size={18} /> {L(step.prompt)}
                </Prompt>
              )}
              <ReplyOptions options={step.options} order={order} onPick={pick} icon={Mic} />
            </>
          )}
          {!answered && !allSpoken && <Waiting>{t('speaking', { name: L(caller?.name) })}</Waiting>}
          {answered && (
            <OptionFeedback
              options={step.options}
              picked={answered.pick}
              dilemma={!!step.dilemma}
              kpiLabels={kpiLabels}
              principle={step.principle}
              delay={revisit ? 0 : 1600}
            />
          )}
        </Composer>
      )}
    </Shell>
  );
}
